import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Pagination,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  TextField,
  Button,
} from '@mui/material';
import {
  ViewModule,
  ViewList,
  FilterList,
  Search,
} from '@mui/icons-material';
import ClientCard from './ClientCard';
import ClientFilters from './ClientFilters';
import { Client, ClientSearchFilters } from '@/types';
import { ClientService } from '@/services/clientService';
import { QueryDocumentSnapshot } from 'firebase/firestore';

interface ClientListProps {
  clients?: Client[];
  loading?: boolean;
  error?: string | null;
  onClientClick?: (client: Client) => void;
  onClientEdit?: (client: Client) => void;
  onClientDelete?: (client: Client) => void;
  showFilters?: boolean;
  filters?: ClientSearchFilters;
  onFilterChange?: (filters: ClientSearchFilters) => void;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  compact?: boolean;
  enableInfiniteScroll?: boolean;
  pagination?: {
    page: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
    onPageChange: (page: number) => void;
  };
}

const ClientList: React.FC<ClientListProps> = ({
  clients: externalClients,
  loading: externalLoading,
  error: externalError,
  onClientClick,
  onClientEdit,
  onClientDelete,
  showFilters = true,
  filters: externalFilters,
  onFilterChange: externalOnFilterChange,
  viewMode: externalViewMode,
  onViewModeChange,
  compact = false,
  enableInfiniteScroll = false,
  pagination,
}) => {
  const [clients, setClients] = useState<Client[]>(externalClients || []);
  const [loading, setLoading] = useState(externalLoading || false);
  const [error, setError] = useState<string | null>(externalError || null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(externalViewMode || 'grid');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [filters, setFilters] = useState<ClientSearchFilters>(externalFilters || {});
  const [searchTerm, setSearchTerm] = useState(externalFilters?.searchTerm || '');
  const [currentPage, setCurrentPage] = useState(pagination?.page || 1);
  const [totalPages, setTotalPages] = useState(pagination?.totalPages || 1);
  const [sort, setSort] = useState<{ field: 'createdAt' | 'updatedAt' | 'profile.firstName' | 'profile.lastName'; direction: 'asc' | 'desc' }>({
    field: 'createdAt',
    direction: 'desc',
  });
  
  // Infinite scroll state
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);

  // Use external clients if provided, otherwise fetch
  const isControlled = externalClients !== undefined;

  useEffect(() => {
    if (externalClients) {
      setClients(externalClients);
    }
  }, [externalClients]);

  useEffect(() => {
    if (externalLoading !== undefined) {
      setLoading(externalLoading);
    }
  }, [externalLoading]);

  useEffect(() => {
    if (externalError !== undefined) {
      setError(externalError);
    }
  }, [externalError]);

  useEffect(() => {
    if (externalFilters) {
      setFilters(externalFilters);
      setSearchTerm(externalFilters.searchTerm || '');
    }
  }, [externalFilters]);

  useEffect(() => {
    if (externalViewMode) {
      setViewMode(externalViewMode);
    }
  }, [externalViewMode]);

  const fetchClients = useCallback(async (page: number = 1, append: boolean = false) => {
    if (isControlled) return;

    setLoading(true);
    setError(null);

    try {
      if (enableInfiniteScroll) {
        // Cursor-based pagination for infinite scroll
        const result = await ClientService.listClients(
          filters,
          12,
          append ? lastDoc || undefined : undefined,
          sort.field,
          sort.direction
        );
        
        if (append) {
          setClients((prev) => [...prev, ...result.clients]);
        } else {
          setClients(result.clients);
        }
        
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } else {
        // Page-based pagination
        const response = await ClientService.getClients(filters, page, 12);
        
        if (response.success && response.data) {
          setClients(response.data);
          if (response.pagination) {
            setTotalPages(response.pagination.totalPages);
            setCurrentPage(response.pagination.page);
          }
        } else {
          setError(response.error || 'Failed to load clients');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [filters, sort, isControlled, enableInfiniteScroll, lastDoc]);

  // Initial fetch
  useEffect(() => {
    if (!isControlled) {
      fetchClients(1, false);
    }
  }, [filters, sort.field, sort.direction]);

  // Infinite scroll observer
  useEffect(() => {
    if (!enableInfiniteScroll || !observerTarget.current || !hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchClients(currentPage, true);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(observerTarget.current);

    return () => {
      observer.disconnect();
    };
  }, [enableInfiniteScroll, hasMore, loading, currentPage, fetchClients]);

  const handleFilterChange = (newFilters: ClientSearchFilters) => {
    setFilters(newFilters);
    if (externalOnFilterChange) {
      externalOnFilterChange(newFilters);
    }
    if (!isControlled) {
      setCurrentPage(1);
      setLastDoc(null);
      // fetchClients will be triggered by useEffect [filters]
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    
    // Debounce search
    const timer = setTimeout(() => {
      handleFilterChange({ ...filters, searchTerm: value });
    }, 500);
    
    return () => clearTimeout(timer);
  };

  const handleSortChange = (field: 'createdAt' | 'updatedAt' | 'profile.firstName' | 'profile.lastName') => {
    const newSort = {
      field,
      direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc' as 'asc' | 'desc',
    };
    setSort(newSort);
    if (!isControlled) {
      setCurrentPage(1);
      setLastDoc(null);
    }
  };

  const handleViewModeChange = (newMode: 'grid' | 'list') => {
    setViewMode(newMode);
    if (onViewModeChange) {
      onViewModeChange(newMode);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    if (pagination?.onPageChange) {
      pagination.onPageChange(page);
    } else if (!isControlled) {
      fetchClients(page, false);
    }
  };

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading && clients.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!loading && clients.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No clients found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {Object.keys(filters).length > 0
            ? 'Try adjusting your filters to see more results.'
            : 'Start by adding your first client.'}
        </Typography>
        {showFilters && (
          <Button 
            variant="outlined" 
            onClick={() => setShowFilterPanel(true)}
            sx={{ mt: 2 }}
          >
            Adjust Filters
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          <TextField
            size="small"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ maxWidth: 300 }}
            InputProps={{
              startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
          />
          
          {showFilters && (
            <Tooltip title="Toggle Filters">
              <IconButton 
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                color={showFilterPanel || Object.keys(filters).length > 1 ? 'primary' : 'default'}
              >
                <FilterList />
              </IconButton>
            </Tooltip>
          )}
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sort.field}
              label="Sort By"
              onChange={(e) => handleSortChange(e.target.value as any)}
            >
              <MenuItem value="createdAt">Date Added</MenuItem>
              <MenuItem value="updatedAt">Last Updated</MenuItem>
              <MenuItem value="profile.firstName">First Name</MenuItem>
              <MenuItem value="profile.lastName">Last Name</MenuItem>
            </Select>
          </FormControl>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && handleViewModeChange(newMode)}
            size="small"
          >
            <ToggleButton value="grid">
              <ViewModule />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewList />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {clients.length} client{clients.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Filters Panel */}
      <Collapse in={showFilterPanel}>
        <Box sx={{ mb: 3 }}>
          <ClientFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClose={() => setShowFilterPanel(false)}
          />
        </Box>
      </Collapse>

      {/* Client Grid/List */}
      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {clients.map((client) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={client.id}>
              <ClientCard
                client={client}
                onView={onClientClick}
                onEdit={onClientEdit}
                onDelete={onClientDelete}
                compact={compact}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onView={onClientClick}
              onEdit={onClientEdit}
              onDelete={onClientDelete}
              compact={false}
            />
          ))}
        </Box>
      )}

      {/* Pagination */}
      {!enableInfiniteScroll && pagination && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Infinite Scroll Target */}
      {enableInfiniteScroll && hasMore && (
        <Box ref={observerTarget} sx={{ display: 'flex', justifyContent: 'center', mt: 4, py: 2 }}>
          {loading && <CircularProgress />}
        </Box>
      )}

      {/* Loading Overlay */}
      {loading && clients.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default ClientList;

